import React, { lazy, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import NoteState from "./context/notes/NoteState";
import { GoogleOAuthProvider } from "@react-oauth/google";

// Custom Components
import MinDurationSuspense from "./components/MinDurationSuspense";
import Spinner from "./components/Spinner";
import VoiceControl from "./components/VoiceControl";
import ProtectedRoute from "./components/ProtectedRoute";

// Lazy-loaded components
const Navbar = lazy(() => import("./components/Navbar"));
const Dashboard = lazy(() => import("./components/Dashboard"));
const About = lazy(() => import("./components/About"));
const PublishNotes = lazy(() => import("./components/PublishNotes"));
const Alert = lazy(() => import("./components/Alert"));
const PageLoader = lazy(() => import("./components/PageLoader"));
const Signup = lazy(() => import("./components/Signup"));
const Login = lazy(() => import("./components/Login"));
const Home = lazy(() => import("./components/Home"));
const Footer = lazy(() => import("./components/Footer"));
const Contact = lazy(() => import("./components/Contact"));
const NotFound = lazy(() => import("./components/NotFound"));
const Help = lazy(() => import("./components/Help"));
const FriendsPanel = lazy(() => import("./components/FriendsPanel"));

// Admin and Premium components
const AdminDashboard = lazy(() => import("./components/AdminDashboard"));
const PremiumUpgrade = lazy(() => import("./components/PremiumUpgrade"));
const Profile = lazy(() => import("./components/Profile"));

// Component to conditionally render Navbar, Footer, and VoiceControl
const Layout = ({ children, showAlert }) => {
  const location = useLocation();
  const isAdminDashboard = location.pathname === "/admin/dashboard";
  // Check if the current route is the Friends page (e.g., /:nanoId/friends)
  const isFriendsPage = location.pathname.endsWith("/friends");

  return (
    <div className="flex flex-col min-h-screen">
      {/* Conditionally render VoiceControl, excluding the Friends page */}
      {!isFriendsPage && <VoiceControl />}
      {!isAdminDashboard && <Navbar />}
      <Alert alert={showAlert.alert} />
      <main className="flex-grow">{children}</main>
      {!isAdminDashboard && <Footer />}
    </div>
  );
};

function App() {
  const [alert, setAlert] = useState(null);

  const showAlert = (message, type) => {
    setAlert({ msg: message, type });
    setTimeout(() => setAlert(null), 3000);
  };

  return (
    <NoteState>
      <GoogleOAuthProvider clientId="204650878291-rqldh38rgu06rgrmnru163eq5hhu9arf.apps.googleusercontent.com">
        <Router>
          <MinDurationSuspense fallback={<Spinner />} minDuration={2000}>
            <PageLoader>
              <Layout showAlert={{ alert, showAlert }}>
                <Routes>
                  <Route path="/" element={<Home showAlert={showAlert} />} />
                  {/* User-specific routes using nanoId */}
                  <Route
                    path="/:nanoId/dashboard"
                    element={<Dashboard showAlert={showAlert} />}
                  />
                  <Route
                    path="/:nanoId/profile"
                    element={<Profile showAlert={showAlert} />}
                  />
                  <Route
                    path="/:nanoId/upgrade"
                    element={<PremiumUpgrade showAlert={showAlert} />}
                  />
                  <Route
                    path="/:nanoId/publishnotes"
                    element={<PublishNotes showAlert={showAlert} />}
                  />
                  <Route
                    path="/:nanoId/friends"
                    element={
                      <ProtectedRoute>
                        <FriendsPanel showAlert={showAlert} />
                      </ProtectedRoute>
                    }
                  />
                  {/* Static route for shared note links */}
                  <Route
                    path="/notes/:uid"
                    element={<PublishNotes showAlert={showAlert} />}
                  />
                  {/* Static routes */}
                  <Route path="/about" element={<About showAlert={showAlert} />} />
                  <Route path="/contact" element={<Contact showAlert={showAlert} />} />
                  <Route path="/login" element={<Login showAlert={showAlert} />} />
                  <Route path="/signup" element={<Signup showAlert={showAlert} />} />
                  {/* Help route */}
                  <Route
                    path="/help"
                    element={
                      <ProtectedRoute>
                        <Help showAlert={showAlert} />
                      </ProtectedRoute>
                    }
                  />
                  {/* Protected Admin Route */}
                  <Route
                    path="/admin/dashboard"
                    element={
                      <ProtectedRoute>
                        <AdminDashboard showAlert={showAlert} />
                      </ProtectedRoute>
                    }
                  />
                  {/* Catch-all route for 404 */}
                  <Route path="*" element={<NotFound showAlert={showAlert} />} />
                </Routes>
              </Layout>
            </PageLoader>
          </MinDurationSuspense>
        </Router>
      </GoogleOAuthProvider>
    </NoteState>
  );
}

export default App;